import { useEffect, useState } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import Modal from "../../../components/ui/Modal";
import Skeleton from "../../../components/ui/Skeleton";
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import NumberInput from "../../../components/ui/NumberInput";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import FileUpload from "../../../components/ui/FileUpload";
import StatusBadge from "../../../components/ui/StatusBadge";
import { EmptyState } from "../../../components/ui/FeedbackStates";
import DeleteConfirmationModal from "../../../components/common/DeleteConfirmationModal";
import masterService from "../../../services/masterEntitiesService";
import { resolveBackendUrl } from "../../../services/api";
import { toast } from "react-toastify";
import { Plus, Edit2, Trash2, Truck, Image as ImageIcon } from "lucide-react";
import styles from "../Masters.module.css";

const CATEGORIES = [
  { label: "Parcel & Courier", value: "PARCEL" },
  { label: "Food Delivery", value: "FOOD" },
  { label: "Grocery & Essentials", value: "GROCERY" },
  { label: "Courier Express", value: "COURIER" },
  { label: "Other Services", value: "OTHER" },
];

export default function MasterDeliveryCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [name, setName] = useState("");
  const [category, setCategory] = useState("PARCEL");
  const [colorCode, setColorCode] = useState("var(--color-dark-bg)");
  const [displayOrder, setDisplayOrder] = useState(0);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const data = await masterService.getDeliveryCompanies();
      setCompanies(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load delivery companies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const openAddModal = () => {
    setEditingCompany(null);
    setName("");
    setCategory("PARCEL");
    setColorCode("var(--color-dark-bg)");
    setDisplayOrder(companies.length + 1);
    setLogoFile(null);
    setLogoPreview("");
    setModalOpen(true);
  };

  const openEditModal = (comp) => {
    setEditingCompany(comp);
    setName(comp.name);
    setCategory(comp.category);
    setColorCode(comp.color_code || "var(--color-dark-bg)");
    setDisplayOrder(comp.display_order || 0);
    setLogoFile(null);
    setLogoPreview(comp.logo_url ? resolveBackendUrl(comp.logo_url) : "");
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Company name is required.");
      return;
    }

    if (!editingCompany && !logoFile) {
      toast.error("Logo file is required for new delivery companies.");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("category", category);
      formData.append("color_code", colorCode);
      formData.append("display_order", displayOrder);
      if (logoFile) {
        formData.append("logo", logoFile);
      }

      if (editingCompany) {
        await masterService.updateDeliveryCompany(editingCompany.id, formData);
        toast.success("Delivery company updated successfully!");
      } else {
        await masterService.createDeliveryCompany(formData);
        toast.success("Delivery company created successfully!");
      }

      setModalOpen(false);
      loadCompanies();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to save delivery company.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = async (comp) => {
    try {
      await masterService.updateDeliveryCompany(comp.id, { is_active: !comp.is_active });
      toast.success(`${comp.name} is now ${!comp.is_active ? "Active" : "Inactive"}`);
      loadCompanies();
    } catch (err) {
      console.error(err);
      toast.error("Failed to toggle status.");
    }
  };

  return (
    <AdminLayout title="Master Delivery Companies">
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>
              <Truck size={24} color="var(--color-dark-bg)" />
              Master Delivery Companies
            </h1>
            <p className={styles.subtitle}>
              Global catalog of delivery carriers, food delivery apps, and courier services with verified logos.
            </p>
          </div>
          <Button variant="primary" onClick={openAddModal}>
            <Plus size={16} style={{ marginRight: "4px" }} /> Add Delivery Company
          </Button>
        </div>

        {loading ? (
          <div style={{ padding: "24px" }}>
            <Skeleton variant="card" count={3} />
          </div>
        ) : companies.length === 0 ? (
          <EmptyState
            title="No delivery companies found"
            description="Add your first delivery carrier to enable brand-verified gate pass logging."
            action={
              <Button onClick={openAddModal}>
                <Plus size={15} style={{ marginRight: "4px" }} /> Add Delivery Company
              </Button>
            }
          />
        ) : (
          <div className={styles.grid}>
            {companies.map((c) => (
              <div key={c.id} className={styles.card}>
                <div className={styles.cardTop}>
                  <div className={styles.logoWrap}>
                    {c.logo_url ? (
                      <img
                        src={resolveBackendUrl(c.logo_url)}
                        alt={c.name}
                        className={styles.logoImg}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <ImageIcon size={24} color="#94a3b8" />
                    )}
                  </div>
                  <div className={styles.cardInfo}>
                    <div className={styles.companyName}>
                      {c.name}
                      {c.color_code && (
                        <span
                          style={{
                            display: "inline-block",
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            marginLeft: "6px",
                            backgroundColor: c.color_code,
                          }}
                        />
                      )}
                    </div>
                    <span className={styles.categoryBadge}>{c.category}</span>
                  </div>
                </div>

                <div className={styles.cardBottom} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                  <button
                    type="button"
                    className={styles.statusBadgeBtn}
                    onClick={() => toggleStatus(c)}
                    title={`Click to ${c.is_active ? "deactivate" : "activate"}`}
                  >
                    <StatusBadge
                      status={c.is_active ? "ACTIVE" : "INACTIVE"}
                      label={c.is_active ? "Active" : "Inactive"}
                    />
                  </button>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <Button variant="outline" size="sm" onClick={() => openEditModal(c)}>
                      <Edit2 size={13} />
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => setDeletingCompany(c)}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <DeleteConfirmationModal
          isOpen={!!deletingCompany}
          onClose={() => setDeletingCompany(null)}
          entityType="Delivery Company"
          entityId={deletingCompany?.id}
          entityName={deletingCompany?.name}
          canDelete={true}
          onConfirm={async () => {
            try {
              await masterService.deleteDeliveryCompany(deletingCompany.id);
              toast.success("Delivery company deleted successfully!");
              setDeletingCompany(null);
              loadCompanies();
            } catch (err) {
              console.error(err);
              toast.error(err.message || "Failed to delete delivery company.");
            }
          }}
        />

        {/* Create / Edit Modal */}
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editingCompany ? `Edit ${editingCompany.name}` : "Add Master Delivery Company"}
        >
          <form onSubmit={handleSubmit} className={styles.form} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <FormField label="Company Name" required>
              <TextInput
                required
                placeholder="e.g. Amazon, Swiggy, DHL, BlueDart"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormField>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <FormField label="Category" required>
                <SelectDropdown
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  options={CATEGORIES}
                />
              </FormField>

              <FormField label="Brand Hex Color">
                <TextInput
                  value={colorCode}
                  onChange={(e) => setColorCode(e.target.value)}
                  placeholder="var(--color-dark-bg)"
                  leadIcon={
                    <span
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "3px",
                        backgroundColor: colorCode || "var(--color-dark-bg)",
                        display: "inline-block",
                        border: "1px solid var(--color-border)",
                      }}
                    />
                  }
                />
              </FormField>
            </div>

            <FormField label="Display Order Priority">
              <NumberInput
                value={displayOrder}
                onChange={(e) => setDisplayOrder(Number(e.target.value) || 0)}
                min={0}
                max={999}
              />
            </FormField>

            <FormField
              label="Company Brand Logo"
              required={!editingCompany}
              helperText="PNG, WebP, or JPG under 500 KB"
            >
              <FileUpload
                accept="image/png,image/webp,image/jpeg"
                maxSizeMB={0.5}
                value={logoPreview}
                helperText="Upload official logo image"
                onChange={(file, dataUrl) => {
                  setLogoFile(file);
                  setLogoPreview(dataUrl);
                }}
              />
            </FormField>

            <div className={styles.formActions} style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
              <Button variant="outline" onClick={() => setModalOpen(false)} type="button">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting} loading={submitting}>
                {editingCompany ? "Save Changes" : "Create Company"}
              </Button>
            </div>
          </form>
        </Modal>
      </div>
    </AdminLayout>
  );
}
